import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DSSeparator } from '@elliemae/ds-separator';
import { isShallowEqual } from '@frontend/pricing-search';
import { DataProvider } from '../../../common/utils/customHooks/useAppData';
import {
  getClientSettingSelector,
  getRateSelectorAction,
} from '../../../data/lockSummary/actionCreators';
import { closeLoader, openLoader } from '../../../data/screenLoader/actions';
import { AppDispatch } from '../../../store';
import {
  StyledEppsContainerBody,
  StyledEppsWrapper,
} from '../../global/styles/styled';
import { CommentsSection } from '../../LockSummary/components/CommentsSection';
import { GeneralLoanInformation } from '../../LockSummary/components/GeneralLoanInformation/GeneralLoanInformation';
import { InformationGroupSection } from '../../LockSummary/components/InformationGroupSection';
import { getTableDataWithIDs } from '../../LockSummary/components/PricingDetailsModal/helpers';
import { PricingTable } from '../../LockSummary/components/PricingTable';
import { SummaryHeader } from '../../LockSummary/components/SummaryHeader';
import { StatusFailMessage } from '../../LockSummary/components/SummaryHeader/StatusFailMessage';
import Common from '../../../common/services/Common';
import { RootState } from '../../../common/reducers';
import {
  buildPayloadForConversion,
  buildTPOCPayload,
  convertLoanToENCv3,
  sendLoanToTPOC,
} from '../../../common/services/tpocServices';
import ScenarioFooter from '../components/ScenarioFooter';

export const ScenarioSummary = () => {
  const [dataTableContext, setTableDataContext] = useState<any>({
    rawPrice: [],
    adjustedPrice: [],
  });
  const dispatch = useDispatch<AppDispatch>();
  const priceData = useSelector(
    ({
      epps: {
        lockSummary: { rateSelector },
      },
    }) => rateSelector.priceData,
    isShallowEqual,
  );
  const allRatesRowData = useSelector(
    (state: RootState) => state.EPPS?.allRatesRowData,
  );
  const summaryDetails = useSelector(
    (state: RootState) => state.epps?.lockSummary.rateSelector,
  );
  const originalLoanInfo = useSelector(
    (state: RootState) => state.epps?.origin.originalLoanData,
  );
  const loanInfo = useSelector((state: RootState) => state.EPPS?.loanInfo);

  useEffect(() => {
    if (priceData) setTableDataContext(getTableDataWithIDs(priceData));
  }, [priceData]);

  useEffect(() => {
    dispatch(openLoader('Loading'));
    Promise.all([
      dispatch(getRateSelectorAction()),
      dispatch(getClientSettingSelector()),
    ]).then(() => dispatch(closeLoader()));
    window.scrollTo(0, 0);
  }, []);

  const handleCancel = () => Common.closeApplication();

  const handleCreateLoan = async () => {
    if (!loanInfo || Object.keys(loanInfo).length === 0) {
      console.error('[TPOC] Create Loan failed: no loan data available');
      return;
    }
    dispatch(openLoader('Creating Loan'));
    try {
      const payloadToConvert = buildPayloadForConversion(
        allRatesRowData ?? null,
        loanInfo,
      );
      const convertedModel = await convertLoanToENCv3(payloadToConvert);
      const tpocPayload = buildTPOCPayload(
        convertedModel,
        loanInfo,
        summaryDetails,
        originalLoanInfo,
      );
      await sendLoanToTPOC(tpocPayload);
      Common.closeApplication();
    } catch (error) {
      console.error('[TPOC] Create Loan failed:', error);
    } finally {
      dispatch(closeLoader());
    }
  };

  return (
    <>
      <StatusFailMessage />
      <StyledEppsContainerBody>
        <StyledEppsWrapper>
          <DataProvider value={{ dataTableContext, setTableDataContext }}>
            <SummaryHeader />
            <GeneralLoanInformation />
            <DSSeparator
              margin="s"
              type="non-form"
              orientation="horizontal"
              position="initial"
            />
            <PricingTable />
            <InformationGroupSection />
            <CommentsSection />
          </DataProvider>
        </StyledEppsWrapper>
      </StyledEppsContainerBody>
      <ScenarioFooter
        onCancel={handleCancel}
        onNext={handleCreateLoan}
        nextLabel="Create Loan"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default ScenarioSummary;
